import React from 'react';
import { Row, Col } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import SearchableSelectField from 'components/layout/forms/SearchableSelectField';
import sortByTranslation from 'services/sort/sortByTranslation';
import NumberInputField from 'components/layout/forms/NumberInputField';
import TextInputField from 'components/layout/forms/TextInputField';
import IAttribute from 'interfaces/products/IAttribute';
import translations from 'services/translations/translations';
import DeleteIcon from 'components/layout/icons/DeleteIcon';
import {
  PRODUCT_ATTRIBUTES_WITH_LIST,
  productAttributeList,
} from 'services/offers/offerChoices';
import {
  COLOR,
  LIGHT_REFLECTANCE_PERCENTAGE,
  MATERIAL_CLARIFICATION,
  SIZE,
  PERFORATION_PERCENTAGE,
} from './attributeTypes';
import ProductAttributeDropdown from './ProductAttributeDropdown';
import updateAttribute from './updateAttribute';

const ProductAttributeRows = ({
  attributeFormValues,
  attributeTypesList,
  setAttributeFormValues,
}: {
  attributeFormValues: IAttribute[];
  attributeTypesList: string[];
  setAttributeFormValues: (newValues: IAttribute[]) => void;
}): JSX.Element | null => {
  const { t } = useTranslation([translations.common, translations.products]);

  const getAttributeOptions = (x: any) => {
    const attributeOptions = [x.type].concat(attributeTypesList);

    return attributeOptions
      .sort(sortByTranslation(t, `${translations.products}:attributeTypes`))
      .map((at) => (
        <option value={at}>
          {t(`${translations.products}:attributeTypes.${at}`)}
        </option>
      ));
  };

  const updateAttributeRow = (e: any, index: any) => {
    setAttributeFormValues(updateAttribute(attributeFormValues, e, index));
  };

  const deleteRow = (index: any, rows: any) => {
    const list = [...rows];
    list.splice(index, 1);
    setAttributeFormValues(list);
  };

  const getValueField = (x: any, i: any): JSX.Element | null => {
    const getTextField = (type: string) => (
      <TextInputField
        value={x.value}
        label={i === 0 ? t(`${translations.products}:Value`) : undefined}
        required
        id={type}
        name="value"
        onChange={(e: any) => updateAttributeRow(e, i)}
      />
    );

    const getNumberField = (type: string) => (
      <NumberInputField
        value={x.value.toString()}
        label={i === 0 ? t(`${translations.products}:Value`) : undefined}
        required
        id={type}
        name="value"
        onChange={(e: any) => updateAttributeRow(e, i)}
      />
    );

    // Free text, e.g. the size M10 of a bolt
    if ([COLOR, MATERIAL_CLARIFICATION, SIZE].includes(x.type)) {
      return getTextField(x.type);
    }

    const numberFields = [LIGHT_REFLECTANCE_PERCENTAGE, PERFORATION_PERCENTAGE];
    if (numberFields.includes(x.type)) {
      return getNumberField(x.type);
    }

    // The values of the attributes with a list are managed on the admin page of the
    // choice lists
    if (PRODUCT_ATTRIBUTES_WITH_LIST.includes(x.type)) {
      return (
        <ProductAttributeDropdown
          index={i}
          item={x}
          list={productAttributeList(x.type)}
          type={x.type}
          updateAttributeRow={updateAttributeRow}
        />
      );
    }

    return null;
  };

  return (
    <div>
      {attributeFormValues.map((x, i) => (
        <Row className="product-attribute">
          <Col md={3}>
            <SearchableSelectField
              value={x.type}
              label={
                i === 0 ? t(`${translations.products}:Attribute`) : undefined
              }
              required
              id="attributeType"
              name="attributeType"
              onChange={(e: any) => updateAttributeRow(e, i)}
              options={getAttributeOptions(x)}
            />
          </Col>
          <Col md={3}>{getValueField(x, i)}</Col>
          <Col md={3}>
            <DeleteIcon onClick={() => deleteRow(i, attributeFormValues)} />
          </Col>
        </Row>
      ))}
    </div>
  );
};

export default ProductAttributeRows;
